import { List } from 'immutable';
import getIn from 'immutable-less/transmute/getIn';
import * as InternalPropertyDefaults from 'automation-ui-action-features/object-type-properties/InternalPropertyDefaults';
import { ManualEnrollTriggerTypes, DetailedPropertiesTypeKey } from 'automation-ui-log-events/constants/LogEventMessageConstants';
import { getListBranchOptions } from 'automation-ui-log-events/utils/messageOptionUtils';
import { getMostSpecificPathFromKeys, getActionTypeKey, getEnrollTypeKey } from 'automation-ui-log-events/utils/messagePathUtils';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { EnrollTypes, TriggerObjectTypes } from 'automation-ui-log-events/constants/LogEventConstants';
import { getState, getAction, getType, getUnenrollType, getErrorType, getFailType, getSkipReason, getActionConnection } from 'automation-ui-log-events/records/LogEvent/utils';
import { getChatflowId, getClassicTrigger, getClassicTriggerMatchedFrom, getEnrollmentAppId, getDeferredTimestamp, getClassicLogEvent, getActionExpired, getIsRepeatPropertyValue, getAcceptedBranchValue } from 'automation-ui-log-events/records/LogEvent/contextMapUtils';
const listBranchHasNextAction = (logEvent, acceptedListBranchPosition) => {
  const connection = getActionConnection(logEvent);
  if (acceptedListBranchPosition === 0) {
    return !!connection.defaultConnection;
  }
  const listBranches = connection.listBranches;
  /**
   * Need to subtract 1 from acceptedListBranchPosition because the 'no'/default branch
   * is not included in the array of listBranches. getListBranchOptions() adds one to each
   * position to normalize the position of all branches.
   */
  const acceptedListBranchConnection = getIn([acceptedListBranchPosition - 1, 'connection'], listBranches);
  return !!acceptedListBranchConnection;
};
const getListBranchResult = (logEvent, objectTypeId) => {
  const listBranchOptions = getListBranchOptions(logEvent, objectTypeId);
  const hasNextAction = listBranchHasNextAction(logEvent, listBranchOptions.branchPosition);
  if (!hasNextAction) {
    if (listBranchOptions.branchName) {
      return 'emptyBranchWithName';
    }
    if (listBranchOptions.branchPosition === 0) {
      return 'emptyBranchDefaultNo';
    }
    return 'emptyBranchDefaultYes';
  }
  if (listBranchOptions.branchName) {
    return 'acceptedBranch';
  }
  if (listBranchOptions.branchPosition === 0) {
    return 'acceptedBranchDefaultNo';
  }
  return 'acceptedBranchDefaultYes';
};
const getStaticBranchResult = logEvent => {
  if (getAcceptedBranchValue(logEvent)) {
    return 'acceptedBranch';
  }
  return 'acceptedBranchDefaultNo';
};
const getRotateObjectResult = action => {
  if (getIn(['metadata', 'targetProperty'], action) !== InternalPropertyDefaults.HUBSPOT_OWNER) {
    return 'withProperty';
  }
  return undefined;
};
const getDelayResult = logEvent => {
  if (getDeferredTimestamp(logEvent)) {
    return 'withDatetime';
  }
  return undefined;
};
const getAdsAudienceMembershipResult = action => {
  const shouldAdd = getIn(['metadata', 'shouldAdd'], action);
  if (shouldAdd === true) {
    return 'addToAudience';
  } else if (shouldAdd === false) {
    return 'removeFromAudience';
  }
  return undefined;
};
const getWaitUntilResult = logEvent => {
  const actionExpired = getActionExpired(logEvent);
  if (actionExpired) {
    return 'actionExpired';
  }
  return 'default';
};
export const getSpecialType = (logEvent, objectTypeId) => {
  const actionType = getActionTypeKey(logEvent);
  const enrollType = getEnrollTypeKey(logEvent);
  switch (actionType) {
    case ActionTypes.LIST_BRANCH:
      return getListBranchResult(logEvent, objectTypeId);
    case ActionTypes.STATIC_BRANCH:
      return getStaticBranchResult(logEvent);
    case ActionTypes.WAIT_UNTIL:
      return getWaitUntilResult(logEvent);
    case ActionTypes.ROTATE_OBJECT:
      {
        const action = getAction(logEvent);
        return getRotateObjectResult(action);
      }
    case ActionTypes.DELAY:
    case ActionTypes.DELAY_UNTIL_DATE:
      return getDelayResult(logEvent);
    case ActionTypes.ADS_AUDIENCE_MEMBERSHIP:
      {
        const action = getAction(logEvent);
        return getAdsAudienceMembershipResult(action);
      }
    case ActionTypes.SET_PROPERTY:
      return getIsRepeatPropertyValue(logEvent) ? 'REPEAT_PROPERTY_VALUE' : undefined;
    default:
      break;
  }
  switch (enrollType) {
    case EnrollTypes.DYNAMIC_ENROLL:
    case EnrollTypes.RE_ENROLL: // same as BULK_ENROLL
    case EnrollTypes.BULK_ENROLL:
      {
        const trigger = getClassicTrigger(logEvent);
        const triggerMatchedFrom = getClassicTriggerMatchedFrom(logEvent);
        if (trigger) {
          if (triggerMatchedFrom === 'ENROLLMENT_CRITERIA') {
            return `${trigger}__${triggerMatchedFrom}`;
          } else {
            return trigger;
          }
        }
        break;
      }
    case EnrollTypes.MANUAL_ENROLL:
      {
        const appId = getEnrollmentAppId(logEvent);
        if (appId) {
          return ManualEnrollTriggerTypes.INTEGRATION;
        }
        break;
      }
    case EnrollTypes.EXTERNAL_TRIGGER_ENROLL:
      {
        const chatflowId = getChatflowId(logEvent);
        if (chatflowId) {
          return TriggerObjectTypes.CHATFLOW_ID;
        }
        break;
      }
    default:
      break;
  }
  return undefined;
};

// Builds a formulaic array of values to lookup in the lyaml, from most general to most specific:
// [eventType, state, actionOrEnrollType, errorOrSpecialType, objectTypeId]
//
// required eventType: <PRE_ENROLLMENT | ENROLLMENT | UNENROLLMENT | ACTION_EXECUTION>
// required state: `executionState` or `enrollmentState`
//     <SUCCESS | DEFERRED | FAIL_CONTINUE | FAIL_RETRY | FAIL_RESCHEDULE | FAIL_IRREPARABLE>
//     <ENROLLED | UNENROLLED | COMPLETED | FAILED>
// optional action type, enroll type, unenroll type, or classic (hybrid) log event
//     <DELAY | SET_PROPERTY | MANUAL_ENROLL | MANUAL_UNENROLL | ... >
// optional error type or special type (for events needing special case details, like LIST_BRANCH)
//     <ACTION_NOT_AUTHORIZED | FLOW_IS_NOT_ENABLED | acceptBranch | ... | [anything you like]>
// optional objectTypeId: Often will not be coded for in lyaml, object-generic copy is expected further up chain
//     <CONTACT_ID | COMPANY_ID | DEAL_ID | QUOTE_ID | TICKET_ID>
//
// NOTE: Copy will only be generated from entries in lyaml for continuous valid keys.
// i.e.: automation-ui-log-events.ACTION_EXECUTION.FAIL_CONTINUE.SET_PROPERTY.undefined.COMPANY
// will supply copy only as specific as automation-ui-log-events.ACTION_EXECUTION.FAIL_CONTINUE.SET_PROPERTY
export const getLogEventMessageKeys = (logEvent, objectTypeId) => {
  const logEventType = getType(logEvent);
  const state = getState(logEvent);
  const actionType = getActionTypeKey(logEvent);
  const enrollType = getEnrollTypeKey(logEvent);
  const unenrollType = getUnenrollType(logEvent);
  const classicLogEvent = getClassicLogEvent(logEvent);
  const failType = getFailType(logEvent);
  const errorType = getErrorType(logEvent);
  const skipReason = getSkipReason(logEvent);
  const specialType = getSpecialType(logEvent, objectTypeId);
  return List([logEventType, state, actionType || enrollType || unenrollType || classicLogEvent, errorType || failType || skipReason || specialType, objectTypeId]);
};
export const getDetailedPropertiesLogEventMessageKeys = (logEvent, objectTypeId) => {
  const logEventType = getType(logEvent);
  const state = getState(logEvent);
  const actionType = getActionTypeKey(logEvent);
  return List([logEventType, state, actionType, DetailedPropertiesTypeKey, objectTypeId]);
};
export const getLogEventMessage = (logEvent, objectTypeId) => getMostSpecificPathFromKeys(getLogEventMessageKeys(logEvent, objectTypeId));
export const getDetailedPropertiesLogEventMessage = (logEvent, objectTypeId) => getMostSpecificPathFromKeys(getDetailedPropertiesLogEventMessageKeys(logEvent, objectTypeId));